import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import moment from 'moment';
import {
  Box,
  Breadcrumbs,
  Button,
  Chip,
  IconButton,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';

const levelLabel = (level) => {
  if (`${level}` === '2') return 'Advanced';
  if (`${level}` === '1') return 'Intermediate';
  return 'Beginner';
};

const statusLabel = (status) => {
  if (`${status}` === '0') return 'Draft';
  if (`${status}` === '2') return 'Private';
  return 'Published';
};

const Header = ({ className, trainingPlan, onAssign, ...rest }) => {
  const status = statusLabel(trainingPlan.status);
  const savedLabel = trainingPlan.updatedAt
    ? `Saved ${moment(trainingPlan.updatedAt).fromNow()}`
    : 'Saved just now';

  return (
    <Box className={className} {...rest} sx={{ pt: 1 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
      >
        <Breadcrumbs aria-label="breadcrumb" sx={{ color: '#40536b' }}>
          <IconButton
            size="small"
            component={RouterLink}
            to="/trainer/training_plans"
            sx={{ color: '#40536b' }}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Link
            variant="body2"
            color="inherit"
            to="/trainer/training_plans"
            component={RouterLink}
            underline="hover"
          >
            Training Plans
          </Link>
          <Typography variant="body2" sx={{ color: '#0f1d2d', fontWeight: 700 }}>
            {trainingPlan.title}
          </Typography>
        </Breadcrumbs>

        <Stack direction="row" spacing={2} alignItems="center">
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Typography sx={{ color: '#40536b', fontSize: 14 }}>
              {savedLabel}
            </Typography>
            <CheckCircleIcon sx={{ color: '#2dbd70', fontSize: 18 }} />
          </Stack>
          <Button
            variant="contained"
            startIcon={<PersonAddAltIcon />}
            onClick={onAssign}
            sx={{
              px: 3,
              backgroundColor: '#4338ca',
              '&:hover': { backgroundColor: '#3730a3' },
            }}
          >
            Assign to Athletes
          </Button>
        </Stack>
      </Stack>

      <Box sx={{ mt: 3, ml: 3.75 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <Typography sx={{ fontSize: 30, fontWeight: 800, color: '#071120' }}>
            {trainingPlan.title}
          </Typography>
          <EditOutlinedIcon sx={{ color: '#40536b', fontSize: 21 }} />
        </Stack>
        <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap">
          <Chip
            size="small"
            label={status}
            icon={<Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#2dbd70' }} />}
            sx={{
              color: '#118847',
              backgroundColor: '#e8f7ef',
              fontWeight: 700,
              '& .MuiChip-icon': { ml: 1 },
            }}
          />
          <Chip
            size="small"
            label={levelLabel(trainingPlan.level)}
            icon={<SignalCellularAltIcon />}
            sx={{ backgroundColor: '#eef2f7', color: '#40536b' }}
          />
          <Chip
            size="small"
            label={`${trainingPlan?.events?.length ?? 0} Workouts`}
            icon={<FitnessCenterIcon />}
            sx={{ backgroundColor: '#eef2f7', color: '#40536b' }}
          />
          <Chip
            size="small"
            label={`${Math.max(
              1,
              Math.ceil(Math.max(1, ...(trainingPlan?.events ?? []).map((e) => e.day ?? 1)) / 7)
            )} Weeks`}
            icon={<CalendarMonthIcon />}
            sx={{ backgroundColor: '#eef2f7', color: '#40536b' }}
          />
          {(trainingPlan.tags ?? []).slice(0, 1).map((tag) => (
            <Chip
              key={tag}
              size="small"
              label={tag}
              sx={{ backgroundColor: '#eef2f7', color: '#40536b' }}
            />
          ))}
        </Stack>
      </Box>
    </Box>
  );
};

export default Header;
